import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  InputLabel,
} from '@mui/material';
import { Close, WarningAmber, ArrowForward } from '@mui/icons-material';
import { requestMagicLink } from '../api/magicLinkApi';
import { useRecaptcha } from '../hooks/useRecaptcha';
import useTexts from '../hooks/useTexts';

function MagicLinkRequestForm() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { getToken } = useRecaptcha();
  const texts = useTexts();

  const handleResend = async () => {
    setIsLoading(true);
    try {
      const recaptchaToken = await getToken('EDIT_API_KEY');
      await requestMagicLink(email, recaptchaToken ?? '');
    } catch {
      // resend silently
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError(texts.ERR_EMAIL_REQUIRED);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(texts.ERR_EMAIL_INVALID);
      return;
    }

    setIsLoading(true);

    try {
      const recaptchaToken = await getToken('EDIT_API_KEY');
      await requestMagicLink(email, recaptchaToken ?? '');
      setSuccess(true);
    } catch {
      setError(texts.ERR_REQUEST_FAILED);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          mx: 'auto',
          my: { sm: 0, md: 4 },
        }}
      >
        <Box
          sx={{
            backgroundColor: '#ffffff',
            borderLeft: '6px solid #95C620',
            p: 3,
            float: 'left',
            mt: { sm: 0, md: 6 },
            mr: { sm: 0, md: 3 },
            width: { sm: '100%', md: 640 },
            mb: 3,
          }}
        >
          <Box mb={3}>
            <Typography
              variant="body1"
              component="span"
              sx={{
                fontWeight: 700,
              }}
            >
              {texts.LINK_SENT_TITLE}
            </Typography>
            <br />
            <Typography variant="body1" component="span">
              {texts.LINK_SENT_DESC.replace('{email}', email)}{' '}
              {texts.LINK_VALIDITY}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            component="span"
            color="primary.main"
            onClick={isLoading ? undefined : handleResend}
            sx={{
              fontWeight: 'bold',
              textDecoration: 'underline',
              cursor: isLoading ? 'default' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
              fontSize: 16,
            }}
          >
            {isLoading ? texts.SENDING : texts.RESEND_EMAIL}
          </Typography>
        </Box>
        <Box textAlign={{ xs: 'center', md: 'unset' }}>
          <img src="/img/worker.png" alt="PPL" width={350} height={350} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 2, p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 5 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 'bold', mb: 1, lineHeight: 1.2 }}
        >
          {texts.EDIT_API_KEY_TITLE}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ lineHeight: 1.6 }}
        >
          {texts.EDIT_API_KEY_DESC}
        </Typography>
      </Box>

      {/* Error */}
      {error && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            pl: 3,
            pr: 2,
            py: 2,
            backgroundColor: '#ffffff',
            borderLeft: '6px solid',
            borderColor: 'error.main',
            mb: 4,
          }}
        >
          <WarningAmber
            sx={{ color: 'error.main', mt: '2px', flexShrink: 0 }}
          />
          <Typography variant="body1" sx={{ color: '#1D1D1D' }}>
            {error}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setError('')}
            sx={{ flexShrink: 0, mt: '-4px' }}
          >
            <Close />
          </IconButton>
        </Box>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} style={{ maxWidth: 444 }}>
        {/* Field: Email */}
        <Box sx={{ mb: 3 }}>
          <InputLabel shrink={false} required sx={{ mb: 1 }}>
            {texts.EMAIL_LABEL}
          </InputLabel>
          <TextField
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            error={!!error}
          />
        </Box>

        {/* Submit Button */}
        <Box sx={{ pt: 4 }}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.main', boxShadow: 'none' },
              py: 3,
              px: 3.5,
              justifyContent: 'space-between',
            }}
          >
            {isLoading ? (
              <>
                <CircularProgress
                  size={20}
                  sx={{ mr: 1, color: 'common.white' }}
                />{' '}
                {texts.SENDING}
              </>
            ) : (
              <>
                <span>{texts.SEND_LINK}</span>
                <ArrowForward />
              </>
            )}
          </Button>
        </Box>
      </form>
    </Box>
  );
}

export default MagicLinkRequestForm;
